import { Box, useThemeUI } from "theme-ui";
import castArray from "lodash/fp/castArray";
import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import head from "lodash/fp/head";
import isNil from "lodash/fp/isNil";
import map from "lodash/fp/map";
import negate from "lodash/fp/negate";

import { Button } from "components";
import ToolTip from "components/ToolTip";
import { ChevronLeftIcon, CheckmarkIcon, _InfoIcon } from "components/icons";
import { FieldsSelector } from "components/Forms";
import { getNestedFieldNames } from "utils";
import * as styles from "components/Forms/ButtonOptions/ButtonOptions.styles";

export default function GoalieGearColorOptions({
  name,
  userChoices,
  userChoiceValues,
  label,
  fields = [],
  setTouchedField,
  focusedField: focusedFields = [],
  setFocusedField,
  goBackFocusedFields,
  toolTip,
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const [_ignore, focusedField] = focusedFields;

  const filteredFields = fields.filter(
    (field) => !focusedField || field.name === focusedField
  );

  const [displaySubFields, ..._restFocuesdFields] = [
    ...focusedFields,
  ].reverse();

  // Used to add toolTip, toolTip-modal and other items to to top level Button
  const setOfGroupedOptionButton = flow(
    castArray,
    map((option) => {
      // TODO: Check against has allrequired values (same as tick)
      const hasSelectedAllValues = false;

      return {
        key: option.label,
        toolTip: option?.toolTip,
        component: (
          <Button
            key={option.label}
            variant="secondary"
            size={isMobile ? "medium" : "large"}
            iconBefore={hasSelectedAllValues && <CheckmarkIcon />}
            isRound
            isSelected={hasSelectedAllValues}
            disabled={option.isDisabled}
            onClick={() => setFocusedField(name)}
            sx={{ userSelect: "none" }}
          >
            {option.label}
          </Button>
        ),
      };
    })
  )({ label, toolTip });

  if (!displaySubFields) {
    return setOfGroupedOptionButton.map(
      ({ key, toolTip, component, modal }) => {
        if (modal) {
          return (
            <Box key={key} sx={styles.optionButtonMobileContainer}>
              {component}
              {modal}
            </Box>
          );
        }
        if (toolTip) {
          return (
            <ToolTip key={key} toolTip={toolTip}>
              {component}
            </ToolTip>
          );
        }

        return component;
      }
    );
  }

  return (
    <>
      {!focusedField && (
        <Button
          variant="secondary"
          size={isMobile ? "medium" : "large"}
          iconBefore={<ChevronLeftIcon />}
          aria-label="go back"
          isRound
          onClick={goBackFocusedFields}
        />
      )}

      {filteredFields.map((field) => {
        const userChoice = flow(
          get("name"),
          getNestedFieldNames,
          map((fieldName) => ({
            userChoiceValue: userChoiceValues[fieldName],
            userChoice: userChoices[fieldName],
          })),
          filter(flow(get("userChoiceValue"), negate(isNil))),
          head,
          get("userChoice")
        )(field);

        const amendedUserChoices = {
          ...userChoices,
          [field.name]: userChoice,
        };

        // !TODO This should really be in the JSON
        const moveOptionsToTop = ["White", "Black"];

        const sortedOptions = field.options.sort((a, b) => {
          const aIndex = moveOptionsToTop.indexOf(a.value);
          const bIndex = moveOptionsToTop.indexOf(b.value);

          // If both values are in moveOptionsToTop, preserve their relative order
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          // If only a's value is in moveOptionsToTop, move a to top
          if (aIndex !== -1) return -1;
          // If only b's value is in moveOptionsToTop, move b to top
          if (bIndex !== -1) return 1;
          // Otherwise maintain original order
          return 0;
        });

        return (
          <FieldsSelector
            key={field.name}
            component={field.component}
            name={field.name}
            label={field.label}
            userChoices={amendedUserChoices}
            userChoiceValues={userChoiceValues}
            options={sortedOptions}
            toolTip={field.toolTip}
            focusedField={_restFocuesdFields}
            setFocusedField={(fieldName) => setFocusedField(fieldName)}
            goBackFocusedFields={goBackFocusedFields}
            setTouchedField={() => setTouchedField(field.name)}
            defaultValueToSetOnRender={field.defaultValueToSetOnRender}
            resetButtonOption={[
              "glove-color-colors",
              "blocker-color-colors",
            ].includes(name)}
          />
        );
      })}
    </>
  );
}
